/**
 * Neon Battle Royale
 * Battle Pass Cart Protection
 *
 * Handles:
 * - Required policy acceptance before checkout
 * - Checkout locking for invalid Battle Pass quantities
 * - Reliable Shopify checkout submission
 * - Cart note and cart attribute preservation
 * - Accessible agreement validation
 * - Shopify Theme Editor section reloads
 */

(() => {
  'use strict';

  const CART_SELECTOR = '[data-nbr-cart]';
  const FORM_SELECTOR = '[data-nbr-cart-form]';
  const TERMS_SELECTOR = '[data-nbr-cart-terms]';
  const AGREEMENT_SELECTOR = '[data-nbr-cart-agreement]';
  const CHECKOUT_SELECTOR = '[data-nbr-cart-checkout]';
  const CHECKOUT_TEXT_SELECTOR =
    '[data-nbr-cart-checkout-text]';
  const CHECKOUT_INTENT_SELECTOR =
    '[data-nbr-cart-checkout-intent]';

  const initializedCarts = new WeakSet();
  const cartControllers = new WeakMap();

  class NBRCart {
    constructor(element) {
      this.element = element;
      this.form = element.querySelector(FORM_SELECTOR);
      this.termsCheckbox =
        element.querySelector(TERMS_SELECTOR);
      this.agreement =
        element.querySelector(AGREEMENT_SELECTOR);
      this.checkoutButton =
        element.querySelector(CHECKOUT_SELECTOR);
      this.checkoutText =
        element.querySelector(CHECKOUT_TEXT_SELECTOR);

      this.requireTerms =
        element.dataset.requireTerms === 'true';

      this.hasTicketItems =
        element.dataset.hasTicketItems === 'true';

      this.hasInvalidTicketQuantity =
        element.dataset.invalidTicketQuantity === 'true';

      this.lastSubmitter = null;
      this.isSubmitting = false;

      this.handleTermsChange =
        this.handleTermsChange.bind(this);
      this.handleFormSubmit =
        this.handleFormSubmit.bind(this);
      this.handleFormClick =
        this.handleFormClick.bind(this);
      this.handleAgreementInput =
        this.handleAgreementInput.bind(this);

      this.initialize();
    }

    initialize() {
      if (!this.form || !this.checkoutButton) {
        return;
      }

      this.bindEvents();
      this.resetCheckoutState();
    }

    bindEvents() {
      this.form.addEventListener(
        'submit',
        this.handleFormSubmit
      );

      this.form.addEventListener(
        'click',
        this.handleFormClick
      );

      if (this.termsCheckbox) {
        this.termsCheckbox.addEventListener(
          'change',
          this.handleTermsChange
        );

        this.termsCheckbox.addEventListener(
          'input',
          this.handleAgreementInput
        );
      }
    }

    handleFormClick(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const submitter = event.target.closest(
        'button[type="submit"], input[type="submit"]'
      );

      if (submitter && this.form.contains(submitter)) {
        this.lastSubmitter = submitter;
      }
    }

    handleTermsChange() {
      this.clearAgreementError();
      this.updateCheckoutState();
    }

    handleAgreementInput() {
      if (this.termsCheckbox?.checked) {
        this.clearAgreementError();
        this.updateCheckoutState();
      }
    }

    handleFormSubmit(event) {
      const submitter =
        event.submitter || this.lastSubmitter;

      const isCheckoutSubmission =
        submitter?.name === 'checkout' ||
        submitter?.matches?.(CHECKOUT_SELECTOR);

      this.lastSubmitter = null;

      if (!isCheckoutSubmission) {
        return;
      }

      /*
       * Stop the browser's first submission so the checkout intent can be
       * preserved in a hidden input before the visible checkout button is
       * disabled. Disabling the button too early removes name="checkout"
       * from the submitted form and causes Shopify to reload the cart.
       */
      event.preventDefault();

      if (this.isSubmitting) {
        return;
      }

      if (this.hasInvalidTicketQuantity) {
        this.setCheckoutError(
          'Remove and re-add any Battle Pass showing a quantity above one.'
        );

        this.scrollToCartWarning();
        return;
      }

      if (
        this.requireTerms &&
        this.termsCheckbox &&
        !this.termsCheckbox.checked
      ) {
        this.showAgreementError();
        return;
      }

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      this.clearAgreementError();
      this.removeGeneratedError();
      this.ensureCheckoutIntent();

      this.isSubmitting = true;
      this.setProcessingState();

      /*
       * Submit through the native form method. This bypasses this submit
       * listener while preserving the cart note, cart attributes, line-item
       * quantities and the hidden checkout instruction.
       */
      window.setTimeout(() => {
        try {
          HTMLFormElement.prototype.submit.call(this.form);
        } catch (error) {
          this.isSubmitting = false;
          this.resetCheckoutState();

          this.setCheckoutError(
            'Checkout could not be opened. Please refresh the page and try again.'
          );

          console.error(
            '[NBR Cart] Checkout submission failed:',
            error
          );
        }
      }, 0);
    }

    ensureCheckoutIntent() {
      let checkoutIntent = this.form.querySelector(
        CHECKOUT_INTENT_SELECTOR
      );

      if (!checkoutIntent) {
        checkoutIntent =
          document.createElement('input');

        checkoutIntent.type = 'hidden';
        checkoutIntent.name = 'checkout';
        checkoutIntent.value = 'Checkout';
        checkoutIntent.dataset.nbrCartCheckoutIntent =
          'true';

        this.form.appendChild(checkoutIntent);
      }
    }

    updateCheckoutState() {
      if (
        !this.checkoutButton ||
        this.isSubmitting
      ) {
        return;
      }

      if (this.hasInvalidTicketQuantity) {
        this.disableCheckout('Review Battle Passes');
        return;
      }

      if (
        this.requireTerms &&
        this.termsCheckbox &&
        !this.termsCheckbox.checked
      ) {
        this.disableCheckout(
          'Accept Terms to Continue'
        );
        return;
      }

      this.enableCheckout(
        'Proceed to Secure Checkout'
      );
    }

    resetCheckoutState() {
      this.isSubmitting = false;

      this.checkoutButton?.removeAttribute(
        'aria-busy'
      );

      this.form
        ?.querySelector(CHECKOUT_INTENT_SELECTOR)
        ?.remove();

      this.updateCheckoutState();
    }

    disableCheckout(text) {
      this.checkoutButton.disabled = true;

      this.checkoutButton.setAttribute(
        'aria-disabled',
        'true'
      );

      this.checkoutButton.dataset.nbrCheckoutState =
        'locked';

      if (this.checkoutText) {
        this.checkoutText.textContent = text;
      }
    }

    enableCheckout(text) {
      this.checkoutButton.disabled = false;

      this.checkoutButton.setAttribute(
        'aria-disabled',
        'false'
      );

      this.checkoutButton.removeAttribute(
        'aria-busy'
      );

      this.checkoutButton.dataset.nbrCheckoutState =
        'ready';

      if (this.checkoutText) {
        this.checkoutText.textContent = text;
      }
    }

    setProcessingState() {
      this.checkoutButton.disabled = true;

      this.checkoutButton.setAttribute(
        'aria-disabled',
        'true'
      );

      this.checkoutButton.setAttribute(
        'aria-busy',
        'true'
      );

      this.checkoutButton.dataset.nbrCheckoutState =
        'processing';

      if (this.checkoutText) {
        this.checkoutText.textContent =
          'Opening Secure Checkout…';
      }
    }

    showAgreementError() {
      if (!this.termsCheckbox || !this.agreement) {
        return;
      }

      this.agreement.classList.add(
        'has-agreement-error'
      );

      this.termsCheckbox.setAttribute(
        'aria-invalid',
        'true'
      );

      this.setCheckoutError(
        'Accept the event policies before continuing to checkout.'
      );

      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.agreement.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'center'
      });

      window.setTimeout(
        () => {
          this.termsCheckbox.focus({
            preventScroll: true
          });
        },
        reducedMotion ? 0 : 350
      );
    }

    clearAgreementError() {
      this.agreement?.classList.remove(
        'has-agreement-error'
      );

      this.termsCheckbox?.removeAttribute(
        'aria-invalid'
      );

      this.removeGeneratedError();
    }

    setCheckoutError(message) {
      const existingWarning =
        this.element.querySelector(
          '[data-nbr-cart-generated-error]'
        );

      if (existingWarning) {
        existingWarning.textContent = message;
        return;
      }

      const warning = document.createElement('p');

      warning.className =
        'nbr-cart-summary__generated-error';

      warning.dataset.nbrCartGeneratedError =
        'true';

      warning.setAttribute('role', 'alert');
      warning.setAttribute(
        'aria-live',
        'assertive'
      );

      warning.textContent = message;

      this.checkoutButton.insertAdjacentElement(
        'beforebegin',
        warning
      );
    }

    removeGeneratedError() {
      this.element
        .querySelector(
          '[data-nbr-cart-generated-error]'
        )
        ?.remove();
    }

    scrollToCartWarning() {
      const warning =
        this.element.querySelector(
          '.nbr-cart__warning'
        ) ||
        this.element.querySelector(
          '.nbr-cart-summary__checkout-warning'
        );

      if (!warning) {
        return;
      }

      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      warning.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'center'
      });
    }

    destroy() {
      this.form?.removeEventListener(
        'submit',
        this.handleFormSubmit
      );

      this.form?.removeEventListener(
        'click',
        this.handleFormClick
      );

      this.termsCheckbox?.removeEventListener(
        'change',
        this.handleTermsChange
      );

      this.termsCheckbox?.removeEventListener(
        'input',
        this.handleAgreementInput
      );

      this.removeGeneratedError();

      this.form
        ?.querySelector(CHECKOUT_INTENT_SELECTOR)
        ?.remove();
    }
  }

  const initializeCart = (element) => {
    if (!element || initializedCarts.has(element)) {
      return;
    }

    const controller = new NBRCart(element);

    initializedCarts.add(element);
    cartControllers.set(element, controller);
  };

  const initializeCarts = (scope = document) => {
    if (scope.matches?.(CART_SELECTOR)) {
      initializeCart(scope);
    }

    scope
      .querySelectorAll?.(CART_SELECTOR)
      .forEach(initializeCart);
  };

  const resetCarts = (scope = document) => {
    if (scope.matches?.(CART_SELECTOR)) {
      cartControllers
        .get(scope)
        ?.resetCheckoutState();
    }

    scope
      .querySelectorAll?.(CART_SELECTOR)
      .forEach((cart) => {
        cartControllers
          .get(cart)
          ?.resetCheckoutState();
      });
  };

  const destroyCarts = (scope) => {
    const carts = [];

    if (scope.matches?.(CART_SELECTOR)) {
      carts.push(scope);
    }

    scope
      .querySelectorAll?.(CART_SELECTOR)
      .forEach((cart) => {
        carts.push(cart);
      });

    carts.forEach((cart) => {
      cartControllers.get(cart)?.destroy();
      cartControllers.delete(cart);
      initializedCarts.delete(cart);
    });
  };

  const initialize = () => {
    initializeCarts(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }

  window.addEventListener(
    'pageshow',
    () => {
      initializeCarts(document);
      resetCarts(document);
    }
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeCarts(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyCarts(event.target);
    }
  );
})();